import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { useCompanyStore } from "../stores/companyStore";
import { BUSINESS_STATUS_MAP } from "../types/company.types";
import type {
  DispatchCompanyListItem,
  CompanySearchResult,
  BusinessStatusType,
} from "../types/company.types";

const COLUMNS = [
  { key: "companyName", label: "업체명" },
  { key: "businessRegNum", label: "사업자번호" },
  { key: "representativeName", label: "대표자" },
  { key: "phoneNum", label: "연락처" },
  { key: "businessStatus", label: "상태" },
  { key: "registDate", label: "등록일" },
] as const;

type ColumnKey = (typeof COLUMNS)[number]["key"];

interface CompanyTableProps {
  data: CompanySearchResult | undefined;
  isLoading: boolean;
}

export function CompanyTable({ data, isLoading }: CompanyTableProps) {
  const page = useCompanyStore((s) => s.searchParams.page);
  const pageSize = useCompanyStore((s) => s.searchParams.pageSize);
  const setPage = useCompanyStore((s) => s.setPage);
  const openDrawerForEdit = useCompanyStore((s) => s.openDrawerForEdit);

  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const items = data?.dispatchCompanies ?? [];

  const renderCell = (company: DispatchCompanyListItem, key: ColumnKey) => {
    switch (key) {
      case "companyName":
        return (
          <span className="font-medium text-blue-600">
            {company.companyName}
          </span>
        );
      case "businessStatus": {
        const status =
          BUSINESS_STATUS_MAP[company.businessStatus as BusinessStatusType];
        if (!status) return "-";
        return (
          <span
            className={`inline-block rounded-full px-1.5 py-0.5 text-xs ${status.color}`}
          >
            {status.label}
          </span>
        );
      }
      case "registDate":
        return company.registDate
          ? format(new Date(company.registDate), "yyyy-MM-dd")
          : "-";
      default:
        return company[key] ?? "-";
    }
  };

  return (
    <div className="flex flex-initial flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="shrink-0 border-b border-gray-200 bg-white p-3">
        <span className="text-base font-medium text-gray-900">
          배차 업체 목록
        </span>
        <span className="ml-2 text-xs text-gray-500">총 {totalCount}건</span>
      </div>

      <div className="flex-1 overflow-auto bg-white">
        <table className="w-full">
          <thead className="sticky top-0 z-10 border-b border-gray-200 bg-gray-50">
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className="h-10 px-4 py-3 text-left text-xs font-medium uppercase text-gray-500"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {isLoading ? (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  className="h-32 text-center text-xs text-gray-400"
                >
                  조회 중...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  className="h-32 text-center text-xs text-gray-400"
                >
                  검색 결과가 없습니다
                </td>
              </tr>
            ) : (
              items.map((company) => (
                <tr
                  key={company.companyGuid}
                  className="h-10 cursor-pointer hover:bg-gray-50"
                  onDoubleClick={() => openDrawerForEdit(company.companyGuid)}
                >
                  {COLUMNS.map((col) => (
                    <td
                      key={col.key}
                      className="px-4 py-2 text-xs text-gray-700"
                    >
                      {renderCell(company, col.key)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalCount > 0 && (
        <div className="flex shrink-0 items-center justify-between border-t border-gray-200 bg-gray-50 px-3 py-2">
          <span className="text-xs text-gray-700">
            {totalCount}건 중 {(page - 1) * pageSize + 1}-
            {Math.min(page * pageSize, totalCount)}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 py-1 text-xs"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              이전
            </Button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <Button
                key={p}
                variant={p === page ? "default" : "outline"}
                size="sm"
                className={`h-7 min-w-[28px] text-xs ${p === page ? "bg-blue-600 text-white" : ""}`}
                onClick={() => setPage(p)}
              >
                {p}
              </Button>
            ))}
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 py-1 text-xs"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              다음
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
